import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import '../../App.css';
import '../ArrowStyles.css';
import { toPng, toSvg } from 'html-to-image';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const themeStyles = {
  mainStageNode: {
    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
    border: '2px solid #3a7bd5',
    borderRadius: '10px',
    padding: '15px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#ffffff',
    width: 180,
    textAlign: 'center',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
  },
  capabilityNode: {
    background: 'rgba(30, 60, 114, 0.9)',
    border: '2px solid #4a90e2',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '13px',
    color: '#ffffff',
    width: 160,
    textAlign: 'center',
  }
};

const initialNodes = [
  // Core Workflow - Main flow (vertical layout)
  {
    id: 'business-user',
    data: { label: '👤 Business User\n(Problem Owner)' },
    position: { x: 400, y: 50 },
    style: {
      ...themeStyles.mainStageNode,
      background: 'linear-gradient(135deg, #8e24aa 0%, #ab47bc 100%)',
      border: '2px solid #9c27b0',
    },
  },
  {
    id: 'prepare',
    data: { label: '📊 Prepare\nData & Features' },
    position: { x: 400, y: 200 },
    style: themeStyles.mainStageNode,
  },
  {
    id: 'build',
    data: { label: '🔨 Build\nML Models' },
    position: { x: 400, y: 350 },
    style: themeStyles.mainStageNode,
  },
  {
    id: 'model-governance',
    data: { label: '🛡️ Model\nGovernance' },
    position: { x: 400, y: 500 },
    style: {
      ...themeStyles.mainStageNode,
      background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
      border: '2px solid #f44336',
    },
  },
  {
    id: 'deploy',
    data: { label: '🚀 Deploy\nto Production' },
    position: { x: 400, y: 650 },
    style: themeStyles.mainStageNode,
  },
  {
    id: 'predict',
    data: { label: '🎯 Predict\n& Serve' },
    position: { x: 400, y: 800 },
    style: themeStyles.mainStageNode,
  },
  {
    id: 'personalized-apps',
    data: { label: '📱 Personalized\nApps' },
    position: { x: 250, y: 950 },
    style: {
      ...themeStyles.mainStageNode,
      background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
      border: '2px solid #4caf50',
    },
  },
  {
    id: 'mlops',
    data: { label: '🔄 MLOps\nMonitor & Optimize' },
    position: { x: 550, y: 950 },
    style: {
      ...themeStyles.mainStageNode,
      background: 'linear-gradient(135deg, #795548 0%, #8d6e63 100%)',
      border: '2px solid #8d6e63',
    },
  },
  {
    id: 'data-sources',
    data: { label: '💾 Data Sources' },
    position: { x: 600, y: 200 },
    style: {
      ...themeStyles.capabilityNode,
      background: 'rgba(46, 204, 113, 0.15)',
      border: '2px solid #27ae60',
    },
  },
  {
    id: 'feedback-left',
    data: { label: '⬆️ Continuous\nFeedback' },
    position: { x: 150, y: 500 },
    style: {
      background: 'rgba(255, 152, 0, 0.1)',
      border: '2px dashed #ff9800',
      borderRadius: '15px',
      padding: '10px 15px',
      fontSize: '12px',
      color: '#ff9800',
      fontWeight: 'bold',
      textAlign: 'center',
    },
  },
  {
    id: 'feedback-right',
    data: { label: '⬆️ Continuous\nFeedback' },
    position: { x: 650, y: 500 },
    style: {
      background: 'rgba(255, 152, 0, 0.1)',
      border: '2px dashed #ff9800',
      borderRadius: '15px',
      padding: '10px 15px',
      fontSize: '12px',
      color: '#ff9800',
      fontWeight: 'bold',
      textAlign: 'center',
    },
  },
];

const initialEdges = [
  // Main workflow pipeline - straight connections (top to bottom)
  {
    id: 'e1',
    source: 'business-user',
    target: 'prepare',
    type: 'straight',
    animated: true,
    style: { stroke: '#9c27b0', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#9c27b0' }
  },
  {
    id: 'e2',
    source: 'prepare',
    target: 'build',
    type: 'straight',
    animated: true,
    style: { stroke: '#3a7bd5', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#3a7bd5' }
  },
  {
    id: 'e3',
    source: 'build',
    target: 'model-governance',
    type: 'straight',
    animated: true,
    style: { stroke: '#3a7bd5', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#3a7bd5' }
  },
  {
    id: 'e4',
    source: 'model-governance',
    target: 'deploy',
    type: 'straight',
    animated: true,
    style: { stroke: '#f44336', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#f44336' }
  },
  {
    id: 'e5',
    source: 'deploy',
    target: 'predict',
    type: 'straight',
    animated: true,
    style: { stroke: '#3a7bd5', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#3a7bd5' }
  },
  
  // Branch to apps and MLOps
  {
    id: 'e6',
    source: 'predict',
    target: 'personalized-apps',
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#4caf50', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#4caf50' }
  },
  {
    id: 'e7',
    source: 'predict',
    target: 'mlops',
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#8d6e63', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#8d6e63' }
  },
  
  // Data source connection
  {
    id: 'e8',
    source: 'data-sources',
    target: 'prepare',
    type: 'smoothstep',
    style: { stroke: '#27ae60', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#27ae60' }
  },
  
  // Continuous feedback loops
  {
    id: 'feedback1',
    source: 'mlops',
    target: 'feedback-right',
    type: 'smoothstep',
    style: { stroke: '#ff9800', strokeWidth: 2, strokeDasharray: '5,5' },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#ff9800' }
  },
  {
    id: 'feedback2',
    source: 'feedback-right',
    target: 'build',
    type: 'smoothstep',
    sourcePosition: 'top',
    targetPosition: 'right',
    style: { stroke: '#ff9800', strokeWidth: 2, strokeDasharray: '5,5' },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#ff9800' }
  },
  {
    id: 'feedback3',
    source: 'personalized-apps',
    target: 'feedback-left',
    type: 'smoothstep',
    style: { stroke: '#ff9800', strokeWidth: 2, strokeDasharray: '5,5' },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#ff9800' }
  },
  {
    id: 'feedback4',
    source: 'feedback-left',
    target: 'prepare',
    type: 'smoothstep',
    sourcePosition: 'top',
    targetPosition: 'left',
    style: { stroke: '#ff9800', strokeWidth: 2, strokeDasharray: '5,5' },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#ff9800' }
  }
];

const PainPointsPanel = ({ onPainPointClick, selectedPainPoint, highlightedStage, onClearHighlights }) => {
  const painPointGroups = [
    {
      stage: 'Problem Definition',
      icon: '👤',
      color: '#8e24aa',
      nodes: ['business-user'],
      painPoints: [
        {
          id: 'business-alignment',
          title: 'Business-Technical Alignment Gap',
          description: 'Misalignment between business goals and ML implementation',
          aiAgentSolution: 'AI agents can help translate business objectives into ML problem definitions',
          businessImpact: '40%+ of ML projects fail due to misaligned objectives',
          automationPotential: '75%'
        }
      ]
    },
    {
      stage: 'Data Preparation',
      icon: '📊',
      color: '#1e3c72',
      nodes: ['prepare', 'data-sources'],
      painPoints: [
        {
          id: 'feature-engineering',
          title: 'Manual Feature Engineering',
          description: '80% of ML project time spent on data preparation',
          aiAgentSolution: 'AI agents can automatically discover and create optimal features',
          businessImpact: '3-6 months per project wasted',
          automationPotential: '75%'
        }
      ]
    },
    {
      stage: 'Model Development',
      icon: '🔨',
      color: '#1e3c72',
      nodes: ['build'],
      painPoints: [
        {
          id: 'model-selection',
          title: 'Suboptimal Model Selection',
          description: 'Teams often miss the best algorithm for their use case',
          aiAgentSolution: 'AI agents can test all algorithms and ensemble methods automatically',
          businessImpact: '20-40% accuracy improvement missed',
          automationPotential: '95%'
        },
        {
          id: 'hyperparameter-tuning',
          title: 'Manual Hyperparameter Tuning',
          description: 'Finding optimal settings is time-consuming and often skipped',
          aiAgentSolution: 'AI agents use advanced optimization to find best parameters automatically',
          businessImpact: '15-30% model performance left on table',
          automationPotential: '100%'
        },
        {
          id: 'multi-objective-tradeoffs',
          title: 'Multi-Objective Optimization Complexity',
          description: 'Balancing accuracy vs explainability vs performance is complex',
          aiAgentSolution: 'AI agents can automatically find Pareto-optimal solutions',
          businessImpact: 'Suboptimal models deployed in 80% of cases',
          automationPotential: '95%'
        }
      ]
    },
    {
      stage: 'Governance & Deployment',
      icon: '🛡️',
      color: '#d32f2f',
      nodes: ['model-governance', 'deploy'],
      painPoints: [
        {
          id: 'governance-bottleneck',
          title: 'Manual Model Governance',
          description: 'Model validation and compliance checks slow deployment',
          aiAgentSolution: 'AI agents can automate compliance checks and generate audit reports',
          businessImpact: '2-6 months additional deployment time',
          automationPotential: '85%'
        },
        {
          id: 'production-code-gap',
          title: 'Data Science Code Not Production-Ready',
          description: 'Research code lacks scalability, error handling, and enterprise standards',
          aiAgentSolution: 'AI agents can automatically refactor and optimize code for production deployment',
          businessImpact: '3-8 weeks additional engineering work per model',
          automationPotential: '90%'
        },
        {
          id: 'ml-engineering-translation',
          title: 'ML-Engineering Translation Barrier',
          description: 'Data scientists and engineers speak different languages, causing deployment delays',
          aiAgentSolution: 'AI agents can bridge the gap by generating engineering documentation and deployment configs',
          businessImpact: 'Communication overhead adds 40-60% to deployment timeline',
          automationPotential: '80%'
        },
        {
          id: 'time-to-market',
          title: 'Slow Time-to-Market',
          description: 'Models take months to deploy, missing business opportunities',
          aiAgentSolution: 'AI agents can automate end-to-end pipeline from idea to production',
          businessImpact: '$2-5M lost revenue per delayed quarter',
          automationPotential: '85%'
        }
      ]
    },
    {
      stage: 'Production & Apps',
      icon: '📱',
      color: '#388e3c',
      nodes: ['predict', 'personalized-apps'],
      painPoints: [
        {
          id: 'personalization-complexity',
          title: 'Department-Specific App Development',
          description: 'Each department needs different interfaces and explanations',
          aiAgentSolution: 'AI agents can auto-generate personalized dashboards and explanations',
          businessImpact: '$50K-200K per custom app development',
          automationPotential: '80%'
        }
      ]
    },
    {
      stage: 'MLOps',
      icon: '🔄',
      color: '#795548',
      nodes: ['mlops'],
      painPoints: [
        {
          id: 'monitoring-drift',
          title: 'Model Performance Decay',
          description: 'Models degrade over time without proper monitoring',
          aiAgentSolution: 'AI agents continuously monitor and retrain models automatically',
          businessImpact: '5-10% accuracy loss per quarter',
          automationPotential: '85%'
        }
      ]
    }
  ];

  return (
    <div style={{
      background: 'white',
      padding: '20px',
      height: '100%',
      overflowY: 'auto'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
      }}>
        <h3 style={{ 
          color: '#333', 
          margin: 0,
          fontSize: '18px'
        }}>
          🎯 Business Pain Points
        </h3>
        <button
          onClick={onClearHighlights}
          style={{
            padding: '5px 10px',
            background: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Clear All
        </button>
      </div>
      
      <p style={{
        fontSize: '13px',
        color: '#666',
        marginBottom: '15px',
        background: '#f5f5f5',
        padding: '8px',
        borderRadius: '4px'
      }}>
        Click workflow boxes or pain points below for highlighting
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {painPointGroups.map((group) => (
          <div key={group.stage} style={{
            background: highlightedStage === group.stage ? '#e3f2fd' : '#fafafa',
            border: highlightedStage === group.stage ? '2px solid #1976d2' : '1px solid #ddd',
            borderRadius: '8px',
            padding: '12px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '10px',
              cursor: 'pointer'
            }}
            onClick={() => onPainPointClick({ relatedNodes: group.nodes, stage: group.stage })}
            >
              <span style={{ fontSize: '16px', marginRight: '8px' }}>{group.icon}</span>
              <h4 style={{ 
                margin: 0, 
                fontSize: '14px',
                color: group.color
              }}>
                {group.stage}
              </h4>
              <div style={{
                marginLeft: 'auto',
                background: group.color,
                color: 'white',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '11px'
              }}>
                {group.painPoints.length} issue{group.painPoints.length > 1 ? 's' : ''}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {group.painPoints.map(painPoint => (
                <div
                  key={painPoint.id}
                  onClick={() => onPainPointClick({ 
                    ...painPoint, 
                    relatedNodes: group.nodes,
                    stage: group.stage 
                  })}
                  style={{
                    padding: '10px',
                    background: selectedPainPoint?.id === painPoint.id ? '#e3f2fd' : 'white',
                    border: selectedPainPoint?.id === painPoint.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginLeft: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedPainPoint?.id !== painPoint.id) {
                      e.currentTarget.style.background = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedPainPoint?.id !== painPoint.id) {
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h5 style={{ 
                        margin: '0 0 4px 0', 
                        fontSize: '13px',
                        color: '#333'
                      }}>
                        {painPoint.title}
                      </h5>
                      <p style={{ 
                        margin: '0 0 4px 0', 
                        fontSize: '11px',
                        color: '#666',
                        lineHeight: '1.3'
                      }}>
                        {painPoint.description}
                      </p>
                      <div style={{
                        background: '#e8f5e9',
                        padding: '4px',
                        borderRadius: '3px',
                        marginTop: '4px'
                      }}>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '11px',
                          color: '#2e7d32'
                        }}>
                          🤖 {painPoint.aiAgentSolution}
                        </p>
                      </div>
                    </div>
                    <div style={{
                      background: selectedPainPoint?.id === painPoint.id ? '#1976d2' : group.color,
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      marginLeft: '8px'
                    }}>
                      {painPoint.automationPotential}
                    </div>
                  </div>
                  
                  {selectedPainPoint?.id === painPoint.id && (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid #ddd'
                    }}>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <strong style={{ fontSize: '11px', color: '#d32f2f' }}>
                            💰 Impact:
                          </strong>
                          <p style={{ 
                            margin: '2px 0 0 0', 
                            fontSize: '10px',
                            color: '#666'
                          }}>
                            {painPoint.businessImpact}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SimplifiedMlWorkflow = ({ title }) => {
  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedPainPoint, setSelectedPainPoint] = useState(null);
  const [highlightedStage, setHighlightedStage] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const reactFlowWrapper = useRef(null);

  // Click outside handler for export menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('[style*="Export"]')) {
        setShowExportMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const nodeToStageMap = {
    'business-user': 'Problem Definition',
    'prepare': 'Data Preparation',
    'data-sources': 'Data Preparation',
    'build': 'Model Development',
    'model-governance': 'Governance & Deployment',
    'deploy': 'Governance & Deployment',
    'predict': 'Production & Apps',
    'personalized-apps': 'Production & Apps',
    'mlops': 'MLOps',
    'feedback-left': 'MLOps',
    'feedback-right': 'MLOps'
  };

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  const handleNodeClick = useCallback((event, node) => {
    const stage = nodeToStageMap[node.id];
    if (stage) {
      setHighlightedStage(stage);
      setSelectedPainPoint(null);
      
      const stageNodes = Object.keys(nodeToStageMap).filter(
        nodeId => nodeToStageMap[nodeId] === stage
      );
      
      setNodes((currentNodes) => 
        currentNodes.map(n => {
          const originalNode = initialNodes.find(origNode => origNode.id === n.id);
          const baseStyle = originalNode ? originalNode.style : n.style;
          
          return {
            ...n,
            style: {
              ...baseStyle,
              ...(stageNodes.includes(n.id) ? {
                border: '3px solid #ff5722',
                boxShadow: '0 0 15px rgba(255, 87, 34, 0.5)'
              } : {})
            }
          };
        })
      );
      
      if (!isPanelOpen) {
        setIsPanelOpen(true);
      }
    }
  }, [nodeToStageMap, isPanelOpen]);

  const handlePainPointClick = (painPoint) => {
    setSelectedPainPoint(painPoint);
    setHighlightedStage(painPoint.stage);
    
    setNodes((currentNodes) => 
      currentNodes.map(node => {
        const originalNode = initialNodes.find(origNode => origNode.id === node.id);
        const baseStyle = originalNode ? originalNode.style : node.style;
        
        return {
          ...node,
          style: {
            ...baseStyle,
            ...(painPoint.relatedNodes && painPoint.relatedNodes.includes(node.id) ? {
              border: '3px solid #ff5722',
              boxShadow: '0 0 15px rgba(255, 87, 34, 0.5)'
            } : {})
          }
        };
      })
    );
  };

  const clearHighlights = () => {
    setSelectedPainPoint(null);
    setHighlightedStage(null);
    
    setNodes((currentNodes) => 
      currentNodes.map(node => {
        const originalNode = initialNodes.find(origNode => origNode.id === node.id);
        return {
          ...node,
          style: originalNode ? originalNode.style : node.style
        };
      })
    );
  };

  // Export functions
  const downloadImage = async (format) => {
    try {
      console.log('Starting export for format:', format);
      setShowExportMenu(false);
      
      // Wait a moment for menu to close
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Find the ReactFlow viewport specifically
      const viewportElement = document.querySelector('.react-flow__viewport');
      const containerElement = document.querySelector('.react-flow');
      
      if (!viewportElement || !containerElement) {
        console.error('Could not find ReactFlow elements');
        alert('Could not find diagram. Please try refreshing the page.');
        return;
      }

      console.log('Found ReactFlow elements, proceeding with export...');
      
      const timestamp = Date.now();
      const filename = `data-science-workflow-${timestamp}`;

      // Common options for export
      const exportOptions = {
        backgroundColor: '#2c3e50',
        cacheBust: true,
        filter: (node) => {
          // Skip controls, minimap, and export menu
          const classesToSkip = ['react-flow__controls', 'react-flow__minimap'];
          return !classesToSkip.some(className => 
            node.classList && node.classList.contains(className)
          );
        }
      };

      if (format === 'png') {
        console.log('Creating PNG...');
        try {
          // Try html2canvas first as it's more reliable
          const canvas = await html2canvas(containerElement, {
            backgroundColor: '#2c3e50',
            scale: 2,
            logging: true,
            useCORS: true,
            allowTaint: true
          });
          
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${filename}.png`;
            link.href = url;
            link.click();
            URL.revokeObjectURL(url);
            console.log('PNG download triggered');
          });
        } catch (pngError) {
          console.error('PNG generation error:', pngError);
          // Fallback to html-to-image
          try {
            const dataUrl = await toPng(containerElement, {
              ...exportOptions,
              width: 1600,
              height: 1200,
              pixelRatio: 2
            });
            
            const link = document.createElement('a');
            link.download = `${filename}.png`;
            link.href = dataUrl;
            link.click();
          } catch (fallbackError) {
            console.error('Fallback PNG generation also failed:', fallbackError);
            throw pngError;
          }
        }
        
      } else if (format === 'svg') {
        console.log('Creating SVG...');
        try {
          const dataUrl = await toSvg(containerElement, {
            ...exportOptions,
            width: 1600,
            height: 1200
          });
          
          // Create download link
          const link = document.createElement('a');
          link.download = `${filename}.svg`;
          link.href = dataUrl;
          link.click();
          console.log('SVG download triggered');
        } catch (svgError) {
          console.error('SVG generation error:', svgError);
          throw svgError;
        }
        
      } else if (format === 'pdf') {
        console.log('Creating PDF...');
        try {
          // Use html2canvas for PDF generation too
          const canvas = await html2canvas(containerElement, {
            backgroundColor: '#2c3e50',
            scale: 2,
            logging: true,
            useCORS: true,
            allowTaint: true
          });
          
          const imgData = canvas.toDataURL('image/png');
          
          const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
          });
          
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
          const imgX = (pdfWidth - imgWidth * ratio) / 2;
          const imgY = 10;
          
          pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
          pdf.save(`${filename}.pdf`);
          console.log('PDF download triggered');
        } catch (pdfError) {
          console.error('PDF generation error:', pdfError);
          throw pdfError;
        }
      }
      
      console.log('Export completed successfully');
      
    } catch (error) {
      console.error('Export failed:', error);
      
      // Provide more helpful error message
      let errorMessage = 'Export failed. ';
      if (error.message.includes('tainted')) {
        errorMessage += 'This might be due to CORS restrictions. Try using the browser\'s built-in screenshot tool instead.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: '#2c3e50',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#ffffff', 
          padding: '15px', 
          margin: '0',
          fontSize: '24px',
          background: 'rgba(0,0,0,0.2)'
        }}>
          {title || "End to End Journey of a Data Science Workflow"}
        </h2>

        <div 
          ref={reactFlowWrapper}
          style={{ 
            position: 'absolute',
            top: '60px',
            left: 0,
            right: isPanelOpen ? '400px' : 0,
            bottom: 0,
            transition: 'right 0.3s ease'
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            fitView
            fitViewOptions={{
              padding: 0.15,
              minZoom: 0.4,
              maxZoom: 0.9
            }}
            minZoom={0.3}
            maxZoom={2}
            defaultViewport={{ x: 100, y: 0, zoom: 0.7 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 100,
                width: 150,
                backgroundColor: 'rgba(0,0,0,0.3)',
                bottom: 10,
                right: 10
              }}
              nodeColor={(node) => node.style?.background || '#666'}
              nodeStrokeColor={() => '#ffffff'}
              nodeBorderRadius={2}
            />
            <Controls 
              style={{
                bottom: 10,
                left: 10
              }}
            />
            <Background 
              variant="dots" 
              color="rgba(255,255,255,0.1)" 
              gap={20} 
              size={1} 
            />
          </ReactFlow>
        </div>

        <button
          onClick={() => setIsPanelOpen(!isPanelOpen)}
          style={{
            position: 'fixed',
            right: isPanelOpen ? '410px' : '20px',
            top: '80px',
            padding: '10px 16px',
            background: '#ff5722',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 'bold',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            transition: 'all 0.3s ease'
          }}
        >
          <span>{isPanelOpen ? '✕' : '🎯'}</span>
          {isPanelOpen ? ' Close' : ' Pain Points'}
        </button>

        {/* Export Button */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            style={{
              position: 'fixed',
              right: isPanelOpen ? '410px' : '20px',
              top: '130px',
              padding: '10px 16px',
              background: '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              zIndex: 1000,
              transition: 'all 0.3s ease'
            }}
          >
            <span>💾</span> Export
          </button>

          {/* Export Menu */}
          {showExportMenu && (
            <div style={{
              position: 'fixed',
              right: isPanelOpen ? '410px' : '20px',
              top: '180px',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              padding: '8px',
              zIndex: 1001,
              minWidth: '150px'
            }}>
              <button
                onClick={() => downloadImage('png')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  margin: '4px 0',
                  background: 'none',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textAlign: 'left',
                  color: '#333',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                📷 PNG (for PowerPoint)
              </button>
              <button
                onClick={() => downloadImage('svg')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  margin: '4px 0',
                  background: 'none',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textAlign: 'left',
                  color: '#333',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                🎨 SVG (scalable)
              </button>
              <button
                onClick={() => downloadImage('pdf')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  margin: '4px 0',
                  background: 'none',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textAlign: 'left',
                  color: '#333',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                📄 PDF (document)
              </button>
              <div style={{
                borderTop: '1px solid #e0e0e0',
                margin: '8px 0',
                paddingTop: '8px',
                fontSize: '11px',
                color: '#666',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                💡 For animations: Use screen recording<br/>
                (Windows: Win+G, Mac: Cmd+Shift+5)
              </div>
            </div>
          )}
        </div>
        
        <div style={{
          position: 'fixed',
          right: isPanelOpen ? '0' : '-400px',
          top: 0,
          bottom: 0,
          width: '400px',
          transition: 'right 0.3s ease',
          zIndex: 500,
          boxShadow: isPanelOpen ? '-5px 0 20px rgba(0,0,0,0.2)' : 'none'
        }}>
          {isPanelOpen && (
            <PainPointsPanel
              onPainPointClick={handlePainPointClick}
              selectedPainPoint={selectedPainPoint}
              highlightedStage={highlightedStage}
              onClearHighlights={clearHighlights}
            />
          )}
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default SimplifiedMlWorkflow; 